import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useClientNames } from '@/hooks/useClientNames';

interface ClientSearchProps {
  currentClient?: string;
  className?: string;
}

export default function ClientSearch({ currentClient, className = '' }: ClientSearchProps) {
  const { clientNames, loading, fetchClientNames } = useClientNames();
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter client names based on search term
  const filteredClients = clientNames.filter(name =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10); // Limit to 10 suggestions

  // Handle client selection
  const handleClientSelect = (clientName: string) => {
    const encodedClientName = encodeURIComponent(clientName);
    router.push(`/deals/client/${encodedClientName}`);
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // Handle search term change and fetch client names if needed
  const handleSearchChange = async (value: string) => {
    setSearchTerm(value);
    setIsOpen(true);
    setSelectedIndex(-1);
    
    // Only fetch client names when user starts typing and we don't have them yet
    if (value.length > 0 && clientNames.length === 0) {
      await fetchClientNames();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredClients.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredClients[selectedIndex]) {
          handleClientSelect(filteredClients[selectedIndex]);
        } else if (filteredClients.length === 1) {
          handleClientSelect(filteredClients[0]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="block w-full rounded-lg border border-zinc-300 bg-white py-2.5 pl-10 pr-10 text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          placeholder="Search for a client..."
          disabled={loading}
        />
        
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              setIsOpen(false);
              setSelectedIndex(-1);
              inputRef.current?.focus();
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XMarkIcon className="h-5 w-5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300" />
          </button>
        )}
      </div>

      {/* Current client indicator */}
      {currentClient && (
        <div className="mt-2 text-sm text-emerald-600 dark:text-emerald-400">
          Currently viewing: <span className="font-medium">{currentClient}</span>
        </div>
      )}

      {/* Suggestions dropdown */}
      {isOpen && searchTerm && filteredClients.length > 0 && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900 max-h-60 overflow-y-auto">
          {filteredClients.map((clientName, index) => (
            <button
              key={clientName}
              onClick={() => handleClientSelect(clientName)}
              className={`w-full text-left px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 focus:bg-zinc-50 dark:focus:bg-zinc-800/50 focus:outline-none transition-colors ${
                index === selectedIndex ? 'bg-zinc-100 dark:bg-zinc-800' : ''
              } ${
                clientName === currentClient ? 'bg-emerald-500/10' : ''
              }`}
            >
              <div className="text-sm font-medium text-zinc-900 dark:text-white">
                {clientName}
              </div>
              {clientName === currentClient && (
                <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                  Currently viewing
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {isOpen && searchTerm && filteredClients.length === 0 && clientNames.length > 0 && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900 p-4">
          <div className="text-sm text-zinc-500 dark:text-zinc-400 text-center">
            No clients found matching &ldquo;{searchTerm}&rdquo;
          </div>
        </div>
      )}

      {/* Loading state */}
      {isOpen && loading && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900 p-4">
          <div className="text-sm text-zinc-500 dark:text-zinc-400 text-center">
            Loading client names...
          </div>
        </div>
      )}
    </div>
  );
} 